import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideImageCard from './partials/promo-slide-image-card'
import {useIntl} from 'react-intl'
import {remCalc} from '../../../theme/_utils'
import PromoSliderIcons from './promo-slider-icon-arrow'

export const PromoSliderImage = ({heading, sliderGroup, uid}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)
    const prevButtonRef = useRef(null)
    const nextButtonRef = useRef(null)
    const navigationDirection = useRef(null)
    const intl = useIntl()

    const [srAnnouncement, setSrAnnouncement] = useState('')

    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(450)}) and (max-width: ${remCalc(767)})`,
    )

    const slidesToView = {base: 1, sm: 1.3, md: 1.6, lg: 1.7, xl: 1.9}

    if (!sliderGroup || sliderGroup.length === 0) return null

    // swiperjs doesn't work correctly with 3 slides when loop + centeredSlides are enabled
    const slides = [...sliderGroup]
    const needsFakeSlides = slides.length === 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((s) => ({...s, isFake: true}))]
        : slides

    const setBulletA11yState = (realIndex) => {
        const bullets = document.querySelectorAll(
            `#promo-slider-image-${uid} .swiper-pagination-bullet`,
        )
        bullets.forEach((bullet, idx) => {
            if (idx === realIndex) bullet.setAttribute('aria-current', 'true')
            else bullet.removeAttribute('aria-current')
        })
    }

    const announceSlide = (realIndex) => {
        const title = slides?.[realIndex]?.text_block?.title
        const paragraph = slides?.[realIndex]?.text_block.body_copy
        const imgDesc = slides?.[realIndex]?.media?.description
        const label = `${title} ${paragraph}` || imgDesc
        setSrAnnouncement(`${label ? `: ${label}` : ''}`)
    }

    return (
        <Box
            id={`promo-slider-image-${uid}`}
            position="relative"
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-image-container"
        >
            {/*This is used so the screen reader will readout the new image selected on the swiper without stealing focus from the selected controll (prev / next arrows) */}
            <Box srOnly role="status" aria-live="polite" aria-atomic="true">
                {srAnnouncement}
            </Box>

            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Box position="relative">
                {/* note chevronIcon is used all swipers thats why top being overwritten*/}
                <PromoSliderIcons.left
                    ref={prevButtonRef}
                    className={`swiper-button-prev swiper-button-prev-${uid}`}
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.button.prev',
                        defaultMessage: 'swiper button previous icon',
                    })}
                    tabIndex={0}
                    onClick={() => {
                        navigationDirection.current = 'prev'
                        swiperRef.current?.slidePrev()
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            navigationDirection.current = 'prev'
                            if (swiperRef.current) {
                                swiperRef.current.slidePrev()
                            }
                        }
                    }}
                    position="absolute"
                    zIndex={10}
                    sx={{...styles.chevronIcon, top: '50%'}}
                    left={isSmallTablet ? '9.5%' : {base: '4%', md: '18%', lg: '19%', xl: '21%'}}
                />
                <Swiper
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.slider',
                        defaultMessage: 'Swiper',
                    })}
                    modules={[Navigation, Pagination, Keyboard]}
                    navigation={{
                        prevEl: `.swiper-button-prev-${uid}`,
                        nextEl: `.swiper-button-next-${uid}`,
                    }}
                    pagination={{
                        clickable: true,
                        el: `#promo-slider-image-${uid} .promo-slider-pagination`,
                        renderBullet: (index, className) => {
                            if (index < slides.length) {
                                const changeSlideLabel = intl.formatMessage({
                                    id: 'label.promo.swiper.slider.image',
                                    defaultMessage: 'Change slide',
                                })
                                return `<button type="button" class="${className}" aria-label="${changeSlideLabel}" aria-controls="promo-slider-image-${uid}" data-slide-index="${index}"></button>`
                            }
                            return ''
                        },
                    }}
                    keyboard={{
                        enabled: true,
                        onlyInViewport: false,
                        pageUpDown: false,
                    }}
                    loop={true}
                    centeredSlides={true}
                    tabIndex={-1}
                    onInit={(swiper) => {
                        swiperRef.current = swiper
                        const bullets = document.querySelectorAll(
                            `#promo-slider-image-${uid} .swiper-pagination-bullet`,
                        )
                        bullets.forEach((b) => b.setAttribute('tabindex', '-1'))
                        const initialRealIndex = swiper.realIndex % slides.length
                        setBulletA11yState(initialRealIndex)
                        announceSlide(initialRealIndex)

                        // Set all slide links to tabindex -1 first
                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${uid} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link) => {
                            link.setAttribute('tabindex', '-1')
                        })

                        // Then set only the active slide's link to 0
                        const activeSlide = swiper.slides[swiper.activeIndex]
                        if (activeSlide) {
                            const activeLink = activeSlide.querySelector('a')
                            if (activeLink) {
                                activeLink.setAttribute('tabindex', '0')
                            }
                        }
                    }}
                    onSlideChange={(swiper) => {
                        const realIndex = swiper.realIndex % slides.length
                        setBulletA11yState(realIndex)
                        announceSlide(realIndex)

                        // Update tabindex for all slides - set all to -1 first
                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${uid} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link) => {
                            link.setAttribute('tabindex', '-1')
                        })

                        // Then set only the active slide's link to 0
                        const activeSlide = swiper.slides[swiper.activeIndex]
                        if (activeSlide) {
                            const activeLink = activeSlide.querySelector('a')
                            if (activeLink) {
                                activeLink.setAttribute('tabindex', '0')
                            }
                        }

                        if (navigationDirection.current) {
                            // Use requestAnimationFrame for smoother focus transition
                            const direction = navigationDirection.current
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    const targetSlide = swiper.slides[swiper.activeIndex]
                                    if (targetSlide) {
                                        const focusableElement = targetSlide.querySelector('a')
                                        if (focusableElement) {
                                            focusableElement.focus({preventScroll: true})
                                        }
                                    }
                                    // Only clear if this is still the same direction
                                    if (navigationDirection.current === direction) {
                                        navigationDirection.current = null
                                    }
                                }, 100)
                            })
                        }
                    }}
                    breakpoints={{
                        0: {
                            slidesPerView: slidesToView.base,
                            spaceBetween: 0,
                        },
                        450: {
                            slidesPerView: slidesToView.sm,
                            spaceBetween: 16,
                        },
                        768: {
                            slidesPerView: slidesToView.md,
                            spaceBetween: 24,
                        },
                        1024: {
                            slidesPerView: slidesToView.lg,
                            spaceBetween: 75,
                        },
                        1200: {
                            slidesPerView: slidesToView.xl,
                            spaceBetween: 142,
                        },
                    }}
                >
                    {allSlides.map((slider, index) => (
                        <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                            <PromoSlideImageCard
                                title={slider.text_block.title}
                                bodyCopy={slider.text_block.body_copy}
                                button={slider.text_block.button}
                                url={slider?.media?.url}
                                alt={slider?.media?.description}
                                images={slider.images}
                                tabIndex={-1}
                            />
                        </SwiperSlide>
                    ))}
                    <Box
                        className="promo-slider-pagination"
                        role="group"
                        aria-label={intl.formatMessage({
                            id: 'label.promo.swiper.pagination',
                            defaultMessage: `Pagination for promo slider`,
                        })}
                    />
                </Swiper>
                <PromoSliderIcons.right
                    ref={nextButtonRef}
                    className={`swiper-button-next swiper-button-next-${uid}`}
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.button.next',
                        defaultMessage: 'swiper button next icon',
                    })}
                    tabIndex={0}
                    onClick={() => {
                        navigationDirection.current = 'next'
                        swiperRef.current?.slideNext()
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            navigationDirection.current = 'next'
                            if (swiperRef.current) {
                                swiperRef.current.slideNext()
                            }
                        }
                    }}
                    position="absolute"
                    zIndex={10}
                    sx={{...styles.chevronIcon, top: '50%'}}
                    right={isSmallTablet ? '9.5%' : {base: '4%', md: '18%', lg: '19%', xl: '21%'}}
                />
            </Box>
        </Box>
    )
}

PromoSliderImage.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
                description: PropTypes.string,
            }),
        }),
    ),
}
